/** The roles × permissions matrix (§4.2.2). Super Admin is locked. */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Save } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { Alert, Button, Card, Skeleton, StatusChip } from '../../components/ui';

interface RoleRow {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  userCount: number;
  permissions: string[];
}

interface CatalogueRow {
  key: string;
  description: string;
  module: string;
}

export function RolesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, Set<string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const roles = useQuery({
    queryKey: ['roles-matrix'],
    queryFn: () => api<{ data: RoleRow[]; catalogue: CatalogueRow[] }>('/roles'),
  });

  useEffect(() => {
    if (!roles.data) return;
    const next: Record<string, Set<string>> = {};
    for (const role of roles.data.data) next[role.id] = new Set(role.permissions);
    setDraft(next);
  }, [roles.data]);

  const save = useMutation({
    mutationFn: (role: RoleRow) =>
      api(`/roles/${role.id}`, {
        method: 'PATCH',
        body: { permissions: [...(draft[role.id] ?? [])] },
      }),
    onSuccess: () => {
      setError(null);
      setNotice('Permissions saved. Users see the change the next time their session refreshes.');
      void queryClient.invalidateQueries({ queryKey: ['roles-matrix'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the role.'),
  });

  const toggle = (roleId: string, key: string): void => {
    setDraft((current) => {
      const next = new Set(current[roleId] ?? []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...current, [roleId]: next };
    });
  };

  if (roles.isLoading) return <Skeleton className="h-96 w-full" />;
  if (!roles.data) return <Alert tone="critical">Could not load the roles.</Alert>;

  const modules = [...new Set(roles.data.catalogue.map((row) => row.module))];
  const editable = roles.data.data.filter((role) => !role.isSystem);

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">Roles &amp; permissions</h1>
        <p className="text-sm text-slate-500">
          A role is a bundle of permissions. The server checks every request against them — hiding a
          button is not security.
        </p>
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 bg-white pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-900">
                Permission
              </th>
              {roles.data.data.map((role) => (
                <th key={role.id} className="px-2 pb-3 text-center">
                  <p className="text-sm font-semibold text-navy dark:text-white">{role.name}</p>
                  <p className="text-xs font-normal text-slate-500">
                    {role.userCount} user{role.userCount === 1 ? '' : 's'}
                  </p>
                  {role.isSystem && (
                    <span className="mt-1 inline-flex">
                      <StatusChip tone="neutral">
                        <Lock aria-hidden="true" className="h-3 w-3" /> Locked
                      </StatusChip>
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modules.map((module) => (
              <>
                <tr key={module}>
                  <td
                    colSpan={roles.data.data.length + 1}
                    className="sticky left-0 bg-slate-50 px-2 py-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:bg-slate-800"
                  >
                    {module}
                  </td>
                </tr>
                {roles.data.catalogue
                  .filter((row) => row.module === module)
                  .map((row) => (
                    <tr key={row.key} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="sticky left-0 bg-white py-2 pr-4 dark:bg-slate-900">
                        <p className="font-medium">{row.description}</p>
                        <p className="font-mono text-xs text-slate-400">{row.key}</p>
                      </td>
                      {roles.data.data.map((role) => (
                        <td key={role.id} className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-navy focus:ring-brand"
                            aria-label={`${row.description} for ${role.name}`}
                            disabled={role.isSystem}
                            checked={draft[role.id]?.has(row.key) ?? false}
                            onChange={() => toggle(role.id, row.key)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
              </>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="flex flex-wrap gap-2">
        {editable.map((role) => (
          <Button key={role.id} busy={save.isPending} onClick={() => save.mutate(role)}>
            <Save aria-hidden="true" className="h-4 w-4" /> Save {role.name}
          </Button>
        ))}
      </div>
    </div>
  );
}
